import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import type { AnnotationLabel, ClientAnnotation } from "./annotationReducer";
import {
  boundsFromCorners,
  clampToImage,
  containsPoint,
  HANDLES,
  handlePoint,
  moveBounds,
  resizeBounds,
  validateBounds,
  type Handle,
  type PixelBounds,
  type Size,
} from "./geometry";
import {
  distance,
  fitToScreen,
  focusOn,
  imageToScreen,
  panBy,
  rescaleForContainer,
  resetView,
  screenToImage,
  zoomAt,
  type Point,
  type Viewport,
} from "./viewport";

export type Tool = AnnotationLabel | "pan";

export interface WordMarker {
  x: number;
  y: number;
  w: number;
  h: number;
  /** Extracted PDF text; never a fixed caption. */
  text: string;
  /** Text contains "porch" and the box lands on this image at a readable size. */
  verified: boolean;
  flag?: string | null;
}

/** A generated crop (completed revision) outlined on the drawing for review. */
export interface CropOverlay {
  id: string;
  bounds: PixelBounds;
  tag: string;
  rejected: boolean;
  /** Highlighted in the gallery (J / L); drawn with a heavier outline. */
  active?: boolean;
}

/** A pending model proposal drawn as a ghost the reviewer can accept with a click or `A`. */
export interface ProposalGhost {
  id: string;
  kind: "porch" | "hard_negative";
  bounds: PixelBounds;
  score: number;
  /** Position in Tab order (1-based) for the on-canvas tag. */
  index: number;
}

export interface LabelCanvasHandle {
  fit(): void;
  reset(): void;
  zoom(factor: number): void;
  /** Zoom/pan so the given bounds fill about half the viewport. */
  focus(bounds: PixelBounds): void;
}

interface Props {
  image: HTMLImageElement | null;
  imageSize: Size | null;
  annotations: ClientAnnotation[];
  selectedId: string | null;
  tool: Tool;
  minBoxSidePx: number;
  wordMarkers: WordMarker[];
  showWordMarkers: boolean;
  cropOverlays?: CropOverlay[];
  showCropOverlays?: boolean;
  proposals?: ProposalGhost[];
  activeProposalId?: string | null;
  showProposals?: boolean;
  disabled?: boolean;
  onAdd(a: { label: AnnotationLabel; bounds: PixelBounds }): void;
  onUpdate(id: string, bounds: PixelBounds): void;
  onSelect(id: string | null): void;
  /** Reviewer clicked a proposal ghost (first click highlights, clicking the highlighted one accepts — page decides). */
  onClickProposal?(id: string): void;
  onFeedback(message: string): void;
  onScaleChange(scale: number): void;
}

const PROPOSAL_COLORS = { porch: "#16a34a", hard_negative: "#2563eb" } as const;

const COLORS: Record<AnnotationLabel, string> = {
  porch: "#16a34a",
  garage: "#1d4ed8",
  kitchen: "#166534",
  bath: "#7c3aed",
  ignore: "#f59e0b",
  not_porch_manual: "#2563eb",
};

const HANDLE_RADIUS = 6;
const CLICK_SLOP = 3;
/** Drags smaller than this on screen are treated as accidental regardless of zoom. */
const MIN_SCREEN_BOX_PX = 8;

interface DragState {
  mode: "draw" | "move" | "resize" | "pan";
  startScreen: Point;
  startImage: Point;
  lastScreen: Point;
  annotationId?: string;
  handle?: Handle;
  original?: PixelBounds;
  label?: AnnotationLabel;
  /** Proposal ghost under the pointer at press time; a click (no drag) accepts it. */
  ghostId?: string;
  moved: boolean;
}

export const LabelCanvas = forwardRef<LabelCanvasHandle, Props>(function LabelCanvas(props, ref) {
  const {
    image,
    imageSize,
    annotations,
    selectedId,
    tool,
    minBoxSidePx,
    wordMarkers,
    showWordMarkers,
    cropOverlays = [],
    showCropOverlays = true,
    proposals = [],
    activeProposalId = null,
    showProposals = true,
    disabled,
    onAdd,
    onUpdate,
    onSelect,
    onClickProposal,
    onFeedback,
    onScaleChange,
  } = props;

  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [container, setContainer] = useState<Size>({ width: 0, height: 0 });
  const imageSizeRef = useRef<Size | null>(null);
  imageSizeRef.current = imageSize;
  const containerPrev = useRef<Size>({ width: 0, height: 0 });
  const [viewport, setViewport] = useState<Viewport>({ scale: 1, tx: 0, ty: 0 });
  const [dpr, setDpr] = useState(() => window.devicePixelRatio || 1);
  const [preview, setPreviewState] = useState<{ id?: string; label: AnnotationLabel; bounds: PixelBounds } | null>(null);
  const previewRef = useRef<typeof preview>(null);
  const setPreview = useCallback((p: typeof preview) => {
    previewRef.current = p;
    setPreviewState(p);
  }, []);
  const [spaceHeld, setSpaceHeld] = useState(false);
  const [cursor, setCursor] = useState("crosshair");
  const drag = useRef<DragState | null>(null);
  const fittedFor = useRef<string | null>(null);
  /** True until the user zooms/pans; while true, container resizes re-fit instead of preserving scale. */
  const autoFit = useRef(true);

  // Container size tracking; keep the same image point centered on resize.
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const rect = entries[0].contentRect;
      const next = { width: Math.round(rect.width), height: Math.round(rect.height) };
      setContainer((prev) => {
        if (prev.width === next.width && prev.height === next.height) return prev;
        if (prev.width > 0 && prev.height > 0) {
          setViewport((vp) =>
            autoFit.current && imageSizeRef.current
              ? fitToScreen(imageSizeRef.current, next)
              : rescaleForContainer(vp, prev, next),
          );
        }
        containerPrev.current = next;
        return next;
      });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const mq = window.matchMedia(`(resolution: ${window.devicePixelRatio}dppx)`);
    const update = () => setDpr(window.devicePixelRatio || 1);
    mq.addEventListener?.("change", update);
    window.addEventListener("resize", update);
    return () => {
      mq.removeEventListener?.("change", update);
      window.removeEventListener("resize", update);
    };
  }, []);

  // Fit once per image once we know the container size.
  useEffect(() => {
    if (!imageSize || container.width === 0 || container.height === 0) return;
    const key = `${image?.src ?? ""}|${imageSize.width}x${imageSize.height}`;
    if (fittedFor.current === key) return;
    fittedFor.current = key;
    autoFit.current = true;
    setViewport(fitToScreen(imageSize, container));
  }, [image, imageSize, container]);

  useEffect(() => {
    onScaleChange(viewport.scale);
  }, [viewport.scale, onScaleChange]);

  useImperativeHandle(
    ref,
    () => ({
      fit() {
        autoFit.current = true;
        if (imageSize) setViewport(fitToScreen(imageSize, container));
      },
      reset() {
        autoFit.current = false;
        if (imageSize) setViewport(resetView(imageSize, container));
      },
      zoom(factor: number) {
        autoFit.current = false;
        setViewport((vp) => zoomAt(vp, { x: container.width / 2, y: container.height / 2 }, factor));
      },
      focus(bounds: PixelBounds) {
        if (!imageSize || container.width === 0) return;
        autoFit.current = false;
        // Never zoom in past what fit-to-screen would give for the whole image when the region is huge.
        const fit = fitToScreen(imageSize, container);
        const target = focusOn(bounds, container, 0.5);
        setViewport(target.scale < fit.scale ? fit : target);
      },
    }),
    [imageSize, container],
  );

  // Space = temporary pan (ignored while typing).
  useEffect(() => {
    const isTyping = (t: EventTarget | null) =>
      t instanceof HTMLElement && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable);
    const down = (e: KeyboardEvent) => {
      if (e.code === "Space" && !isTyping(e.target)) {
        e.preventDefault();
        setSpaceHeld(true);
      }
    };
    const up = (e: KeyboardEvent) => {
      if (e.code === "Space") setSpaceHeld(false);
    };
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    window.addEventListener("blur", () => setSpaceHeld(false));
    return () => {
      window.removeEventListener("keydown", down);
      window.removeEventListener("keyup", up);
    };
  }, []);

  const effectiveTool: Tool = spaceHeld ? "pan" : tool;

  // Wheel zoom (non-passive so we can prevent page scroll).
  useEffect(() => {
    const el = canvasRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      const anchor = { x: e.clientX - rect.left, y: e.clientY - rect.top };
      const factor = Math.exp(-e.deltaY * (e.deltaMode === 1 ? 0.05 : 0.0015));
      autoFit.current = false;
      setViewport((vp) => zoomAt(vp, anchor, factor));
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  const screenPoint = useCallback((e: { clientX: number; clientY: number }): Point => {
    const rect = canvasRef.current!.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  }, []);

  /** Hit-test in screen space: selected handles first, then the smallest box under the cursor. */
  const hitTest = useCallback(
    (sp: Point): { kind: "handle"; id: string; handle: Handle } | { kind: "box"; id: string } | null => {
      const selected = annotations.find((a) => a.id === selectedId);
      if (selected) {
        for (const h of HANDLES) {
          const p = imageToScreen(viewport, handlePoint(selected.bounds, h));
          if (distance(p, sp) <= HANDLE_RADIUS + 2) return { kind: "handle", id: selected.id, handle: h };
        }
      }
      const ip = screenToImage(viewport, sp);
      let best: ClientAnnotation | null = null;
      for (const a of annotations) {
        if (containsPoint(a.bounds, ip.x, ip.y)) {
          const area = (a.bounds.right - a.bounds.left) * (a.bounds.bottom - a.bounds.top);
          const bestArea = best ? (best.bounds.right - best.bounds.left) * (best.bounds.bottom - best.bounds.top) : Infinity;
          if (area < bestArea) best = a;
        }
      }
      return best ? { kind: "box", id: best.id } : null;
    },
    [annotations, selectedId, viewport],
  );

  /** Smallest pending proposal ghost under a screen point (only when ghosts are shown). */
  const hitProposal = useCallback(
    (sp: Point): ProposalGhost | null => {
      if (!showProposals || proposals.length === 0) return null;
      const ip = screenToImage(viewport, sp);
      let best: ProposalGhost | null = null;
      for (const p of proposals) {
        if (containsPoint(p.bounds, ip.x, ip.y)) {
          const area = (p.bounds.right - p.bounds.left) * (p.bounds.bottom - p.bounds.top);
          const bestArea = best ? (best.bounds.right - best.bounds.left) * (best.bounds.bottom - best.bounds.top) : Infinity;
          if (area < bestArea) best = p;
        }
      }
      return best;
    },
    [proposals, showProposals, viewport],
  );

  const onPointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!imageSize || disabled) return;
    if (e.button !== 0 && e.button !== 1) return;
    const sp = screenPoint(e);
    const ip = screenToImage(viewport, sp);
    try {
      canvasRef.current?.setPointerCapture(e.pointerId);
    } catch {
      // synthetic or already-released pointer
    }
    const panning = e.button === 1 || effectiveTool === "pan";
    if (panning) {
      drag.current = { mode: "pan", startScreen: sp, startImage: ip, lastScreen: sp, moved: false };
      setCursor("grabbing");
      return;
    }
    const hit = hitTest(sp);
    if (hit?.kind === "handle") {
      const a = annotations.find((x) => x.id === hit.id)!;
      drag.current = {
        mode: "resize",
        startScreen: sp,
        startImage: ip,
        lastScreen: sp,
        annotationId: hit.id,
        handle: hit.handle,
        original: a.bounds,
        label: a.label,
        moved: false,
      };
      return;
    }
    if (hit?.kind === "box") {
      const a = annotations.find((x) => x.id === hit.id)!;
      onSelect(hit.id);
      drag.current = {
        mode: "move",
        startScreen: sp,
        startImage: ip,
        lastScreen: sp,
        annotationId: hit.id,
        original: a.bounds,
        label: a.label,
        moved: false,
      };
      return;
    }
    // Empty area: start drawing (any direction). A plain click on a proposal ghost accepts it instead.
    onSelect(null);
    drag.current = {
      mode: "draw",
      startScreen: sp,
      startImage: ip,
      lastScreen: sp,
      label: effectiveTool as AnnotationLabel,
      ghostId: hitProposal(sp)?.id,
      moved: false,
    };
  };

  const onPointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const sp = screenPoint(e);
    const d = drag.current;
    if (!d) {
      if (!imageSize || disabled) return;
      if (effectiveTool === "pan") {
        setCursor("grab");
        return;
      }
      const hit = hitTest(sp);
      if (hit?.kind === "handle") setCursor(cursorForHandle(hit.handle));
      else if (hit?.kind === "box") setCursor("move");
      else if (hitProposal(sp)) setCursor("pointer");
      else setCursor("crosshair");
      return;
    }
    if (!imageSize) return;
    if (distance(sp, d.startScreen) > CLICK_SLOP) d.moved = true;
    if (d.mode === "pan") {
      autoFit.current = false;
      setViewport((vp) => panBy(vp, sp.x - d.lastScreen.x, sp.y - d.lastScreen.y));
      d.lastScreen = sp;
      return;
    }
    const ip = screenToImage(viewport, sp);
    if (d.mode === "draw") {
      const b = clampToImage(boundsFromCorners(d.startImage.x, d.startImage.y, ip.x, ip.y), imageSize);
      setPreview({ label: d.label!, bounds: b });
    } else if (d.mode === "move" && d.original) {
      const dx = (sp.x - d.startScreen.x) / viewport.scale;
      const dy = (sp.y - d.startScreen.y) / viewport.scale;
      setPreview({ id: d.annotationId, label: d.label!, bounds: moveBounds(d.original, dx, dy, imageSize) });
    } else if (d.mode === "resize" && d.original && d.handle) {
      setPreview({ id: d.annotationId, label: d.label!, bounds: resizeBounds(d.original, d.handle, ip.x, ip.y, imageSize) });
    }
  };

  const onPointerUp = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const d = drag.current;
    drag.current = null;
    try {
      canvasRef.current?.releasePointerCapture(e.pointerId);
    } catch {
      // ignore
    }
    const currentPreview = previewRef.current;
    setPreview(null);
    if (!d || !imageSize) return;
    if (d.mode === "pan") {
      setCursor(effectiveTool === "pan" ? "grab" : "crosshair");
      return;
    }
    if (!d.moved) {
      // Click without drag: selection only (already handled on down), or a click on a proposal ghost.
      if (d.mode === "draw" && d.ghostId && onClickProposal) onClickProposal(d.ghostId);
      return;
    }
    if (!currentPreview) return;
    const reason = validateBounds(currentPreview.bounds, imageSize, minBoxSidePx);
    if (d.mode === "draw") {
      if (reason) {
        onFeedback(`Box rejected: ${reason}`);
        return;
      }
      const screenW = (currentPreview.bounds.right - currentPreview.bounds.left) * viewport.scale;
      const screenH = (currentPreview.bounds.bottom - currentPreview.bounds.top) * viewport.scale;
      if (screenW < MIN_SCREEN_BOX_PX || screenH < MIN_SCREEN_BOX_PX) {
        onFeedback(`Box rejected: under ${MIN_SCREEN_BOX_PX}px on screen. Zoom in to mark small features.`);
        return;
      }
      onAdd({ label: d.label!, bounds: currentPreview.bounds });
      return;
    }
    if (d.annotationId) {
      if (reason) {
        onFeedback(`Edit reverted: ${reason}`);
        return;
      }
      onUpdate(d.annotationId, currentPreview.bounds);
    }
  };

  // --- Drawing --------------------------------------------------------------
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || container.width === 0 || container.height === 0) return;
    const w = Math.round(container.width * dpr);
    const h = Math.round(container.height * dpr);
    if (canvas.width !== w || canvas.height !== h) {
      canvas.width = w;
      canvas.height = h;
    }
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.fillStyle = "#e2e8f0";
    ctx.fillRect(0, 0, w, h);

    if (image && imageSize) {
      ctx.setTransform(dpr * viewport.scale, 0, 0, dpr * viewport.scale, dpr * viewport.tx, dpr * viewport.ty);
      ctx.imageSmoothingEnabled = viewport.scale < 1;
      ctx.imageSmoothingQuality = "high";
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, imageSize.width, imageSize.height);
      ctx.drawImage(image, 0, 0);
    }

    // Overlays in CSS pixel space (crisp regardless of zoom).
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    const toScreenRect = (b: PixelBounds) => {
      const a = imageToScreen(viewport, { x: b.left, y: b.top });
      const c = imageToScreen(viewport, { x: b.right, y: b.bottom });
      return { x: a.x, y: a.y, w: c.x - a.x, h: c.y - a.y };
    };

    if (showWordMarkers && imageSize) {
      for (const m of wordMarkers) {
        const r = toScreenRect({ left: m.x, top: m.y, right: m.x + m.w, bottom: m.y + m.h });
        const caption = m.verified ? m.text : `unverified: ${m.text}`;
        ctx.strokeStyle = m.verified ? "rgba(217, 70, 239, 0.9)" : "rgba(180, 83, 9, 0.95)";
        ctx.setLineDash(m.verified ? [3, 3] : [2, 3]);
        ctx.lineWidth = m.verified ? 1 : 1.5;
        ctx.strokeRect(r.x, r.y, Math.max(r.w, 1), Math.max(r.h, 1));
        ctx.setLineDash([]);
        ctx.fillStyle = m.verified ? "rgba(217, 70, 239, 0.9)" : "rgba(180, 83, 9, 0.95)";
        ctx.font = "10px ui-sans-serif, system-ui";
        ctx.fillText(caption.slice(0, 40), r.x, r.y - 2);
      }
    }

    // Generated negative crops: outlined so the reviewer sees exactly what the sampler picked.
    if (showCropOverlays) {
      for (const c of cropOverlays) {
        const r = toScreenRect(c.bounds);
        const color = c.rejected ? "rgba(220, 38, 38, 0.8)" : c.active ? "rgba(15, 23, 42, 1)" : "rgba(71, 85, 105, 0.9)";
        ctx.lineWidth = c.active ? 3 : 1.5;
        ctx.strokeStyle = color;
        ctx.setLineDash(c.active ? [] : [5, 4]);
        ctx.strokeRect(r.x + 0.5, r.y + 0.5, r.w, r.h);
        ctx.setLineDash([]);
        ctx.fillStyle = c.rejected ? "rgba(220, 38, 38, 0.08)" : "rgba(71, 85, 105, 0.06)";
        ctx.fillRect(r.x, r.y, r.w, r.h);
        if (c.rejected) {
          ctx.beginPath();
          ctx.moveTo(r.x, r.y);
          ctx.lineTo(r.x + r.w, r.y + r.h);
          ctx.moveTo(r.x + r.w, r.y);
          ctx.lineTo(r.x, r.y + r.h);
          ctx.stroke();
        }
        ctx.font = "bold 11px ui-sans-serif, system-ui";
        const tag = c.rejected ? `${c.tag} rejected` : c.tag;
        const tw = ctx.measureText(tag).width + 8;
        ctx.fillStyle = color;
        ctx.fillRect(r.x, r.y + r.h - 16, tw, 16);
        ctx.fillStyle = "#fff";
        ctx.fillText(tag, r.x + 4, r.y + r.h - 4);
      }
    }

    // Pending model proposals: translucent ghosts with a score chip. The active one (Tab) is emphasized.
    if (showProposals) {
      for (const p of proposals) {
        const r = toScreenRect(p.bounds);
        const color = PROPOSAL_COLORS[p.kind];
        const active = p.id === activeProposalId;
        ctx.save();
        ctx.globalAlpha = active ? 1 : 0.7;
        ctx.lineWidth = active ? 2.5 : 1.5;
        ctx.strokeStyle = color;
        ctx.setLineDash([4, 4]);
        ctx.strokeRect(r.x + 0.5, r.y + 0.5, r.w, r.h);
        ctx.setLineDash([]);
        ctx.fillStyle = p.kind === "porch" ? "rgba(22, 163, 74, 0.10)" : "rgba(37, 99, 235, 0.08)";
        ctx.fillRect(r.x, r.y, r.w, r.h);
        if (active) {
          // Corner ticks so the active ghost reads as "selected" without real handles.
          const t = 10;
          ctx.lineWidth = 3;
          ctx.beginPath();
          for (const [x, y, dx, dy] of [
            [r.x, r.y, 1, 1],
            [r.x + r.w, r.y, -1, 1],
            [r.x, r.y + r.h, 1, -1],
            [r.x + r.w, r.y + r.h, -1, -1],
          ] as const) {
            ctx.moveTo(x, y + dy * t);
            ctx.lineTo(x, y);
            ctx.lineTo(x + dx * t, y);
          }
          ctx.stroke();
        }
        ctx.font = "bold 11px ui-sans-serif, system-ui";
        const tag = `${p.kind === "porch" ? "P" : "HN"}${p.index} ${Math.round(p.score * 100)}%${active ? "  A / click again: accept · R: reject · Del: dismiss" : ""}`;
        const tw = ctx.measureText(tag).width + 8;
        ctx.fillStyle = color;
        ctx.fillRect(r.x, r.y + r.h, tw, 16);
        ctx.fillStyle = "#fff";
        ctx.fillText(tag, r.x + 4, r.y + r.h + 12);
        ctx.restore();
      }
    }

    let porchIndex = 0;
    const drawBox = (b: PixelBounds, label: AnnotationLabel, selected: boolean, tag: string, ghost: boolean) => {
      const r = toScreenRect(b);
      const color = COLORS[label];
      ctx.lineWidth = selected ? 2.5 : 1.5;
      ctx.strokeStyle = color;
      ctx.setLineDash(ghost ? [6, 4] : label === "ignore" ? [8, 4] : []);
      ctx.strokeRect(r.x + 0.5, r.y + 0.5, r.w, r.h);
      ctx.setLineDash([]);
      ctx.fillStyle = label === "ignore" ? "rgba(245, 158, 11, 0.12)" : label === "porch" ? "rgba(22, 163, 74, 0.08)" : "rgba(37, 99, 235, 0.08)";
      ctx.fillRect(r.x, r.y, r.w, r.h);
      // Tag
      ctx.font = "bold 12px ui-sans-serif, system-ui";
      const tw = ctx.measureText(tag).width + 8;
      ctx.fillStyle = color;
      ctx.fillRect(r.x, r.y - 18, tw, 18);
      ctx.fillStyle = "#fff";
      ctx.fillText(tag, r.x + 4, r.y - 5);
      if (selected) {
        for (const hnd of HANDLES) {
          const p = imageToScreen(viewport, handlePoint(b, hnd));
          ctx.beginPath();
          ctx.arc(p.x, p.y, HANDLE_RADIUS, 0, Math.PI * 2);
          ctx.fillStyle = "#fff";
          ctx.fill();
          ctx.lineWidth = 2;
          ctx.strokeStyle = color;
          ctx.stroke();
        }
      }
    };

    for (const a of annotations) {
      const isPreviewTarget = preview?.id === a.id;
      const bounds = isPreviewTarget && preview ? preview.bounds : a.bounds;
      let tag: string;
      if (a.label === "porch") tag = `Porch ${++porchIndex}`;
      else if (a.label === "ignore") tag = "Ignore";
      else tag = "Hard negative";
      if (a.source === "proposal" && a.untouched) tag += " · auto";
      else if (a.source === "proposal_rejected") tag += " · rejected proposal";
      drawBox(bounds, a.label, a.id === selectedId, tag, isPreviewTarget);
    }
    if (preview && !preview.id) {
      const b = preview.bounds;
      const tag = `${b.right - b.left} x ${b.bottom - b.top} px`;
      drawBox(b, preview.label, false, tag, true);
    }
  }, [image, imageSize, annotations, selectedId, viewport, container, dpr, preview, wordMarkers, showWordMarkers, cropOverlays, showCropOverlays, proposals, activeProposalId, showProposals]);

  return (
    <div ref={containerRef} className="relative h-full w-full overflow-hidden bg-slate-200" data-testid="label-canvas">
      <canvas
        ref={canvasRef}
        className="block h-full w-full touch-none select-none"
        style={{ cursor: disabled ? "not-allowed" : effectiveTool === "pan" && !drag.current ? "grab" : cursor }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onContextMenu={(e) => e.preventDefault()}
      />
      {!image && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-sm text-slate-500">
          {imageSize ? "Loading image…" : "No image selected"}
        </div>
      )}
    </div>
  );
});

function cursorForHandle(h: Handle): string {
  switch (h) {
    case "n":
    case "s":
      return "ns-resize";
    case "e":
    case "w":
      return "ew-resize";
    case "ne":
    case "sw":
      return "nesw-resize";
    case "nw":
    case "se":
      return "nwse-resize";
  }
}
